import React from 'react';
import { APP_COLORS as C, APP_FONT as F } from '../tokens';
import { fmtMoney, fmtPct } from '../helpers';
import type { ExpenseCategory } from '../types';

type Props = {
  categories: ReadonlyArray<ExpenseCategory>;
  total: number;
  deltaPct: number;
  periodLabel?: string;
  onViewDetail?: () => void;
  onHelpful?: (helpful: boolean) => void;
};

const Thumb = ({ down, onClick }: { down?: boolean; onClick?: () => void }) => {
  const [hovered, setHovered] = React.useState(false);
  return (
    <span
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      style={{
        width: 26, height: 26, borderRadius: '50%',
        border: '1px solid ' + (hovered ? C.teal : C.line),
        background: hovered ? C.teal : '#fff',
        display: 'grid', placeItems: 'center', cursor: 'pointer',
      }}
    >
      <svg viewBox="0 0 24 24" width={13} height={13} fill="none"
        stroke={hovered ? '#fff' : C.sub} strokeWidth={2.2}
        style={down ? { transform: 'scaleY(-1)' } : undefined}>
        <path d="M7 10v12H3V10z" />
        <path d="M7 10l5-8c2 0 3 1 3 3v3h5a2 2 0 012 2l-2 10a2 2 0 01-2 2H7" />
      </svg>
    </span>
  );
};

// Build concentric donut arcs using stroke-dasharray
function Donut({ categories }: { categories: ReadonlyArray<ExpenseCategory> }) {
  const r = 15.915;
  const circumference = 100; // dash values are already percentages
  let offset = 25; // start at top
  return (
    <svg viewBox="0 0 42 42" width={100} height={100}>
      <circle cx="21" cy="21" r={r} fill="#fff" stroke={C.lineSoft} strokeWidth="6" />
      {categories.map((cat) => {
        const dash = cat.pct + ' ' + (circumference - cat.pct);
        const el = (
          <circle
            key={cat.label}
            cx="21"
            cy="21"
            r={r}
            fill="transparent"
            stroke={cat.color}
            strokeWidth="6"
            strokeDasharray={dash}
            strokeDashoffset={offset}
            transform="rotate(-90 21 21)"
          />
        );
        offset -= cat.pct;
        return el;
      })}
    </svg>
  );
}

export default function ExpensesDonutCard({
  categories,
  total,
  deltaPct,
  periodLabel = 'Last 30 days',
  onViewDetail,
  onHelpful,
}: Props) {
  const isDown = deltaPct < 0;

  return (
    <div style={{
      background: C.card,
      border: '1px solid ' + C.line,
      borderRadius: 14,
      padding: '20px 22px',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: F.family,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: C.sub, textTransform: 'uppercase' }}>
          Expenses
        </span>
        <span style={{ fontSize: 12, color: C.sub, display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
          {periodLabel}
          <svg viewBox="0 0 24 24" width={12} height={12} fill="none" stroke={C.sub} strokeWidth={2.2}>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </div>

      <p style={{ fontSize: 12.5, color: C.sub, margin: '0 0 6px' }}>Spending by category</p>
      <div style={{ fontSize: 28, fontWeight: 800, color: C.ink, letterSpacing: '-0.015em', fontVariantNumeric: 'tabular-nums', marginBottom: 4 }}>
        {fmtMoney(total)}
      </div>
      <div style={{
        fontSize: 12, marginBottom: 14,
        color: isDown ? C.green : C.red,
        display: 'inline-flex', alignItems: 'center', gap: 4,
      }}>
        <svg viewBox="0 0 24 24" width={12} height={12} fill="none" stroke="currentColor" strokeWidth={2.5}>
          <polyline points={isDown ? '6 9 12 15 18 9' : '18 15 12 9 6 15'} />
        </svg>
        {fmtPct(deltaPct)} vs prior period
      </div>

      {categories.length === 0 ? (
        <div style={{ fontSize: 12.5, color: C.sub, padding: '18px 0', fontStyle: 'italic' }}>
          No category data yet. Categorize transactions to see the breakdown.
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          <div style={{ flexShrink: 0 }}><Donut categories={categories} /></div>
          <div style={{ flex: 1, fontSize: 12 }}>
            {categories.slice(0, 5).map((cat) => (
              <div key={cat.label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: cat.color, flexShrink: 0 }} />
                <span style={{ flex: 1, color: C.ink, fontWeight: 500 }}>{cat.label}</span>
                <span style={{ color: C.sub, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{cat.pct}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginTop: 'auto', paddingTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span onClick={onViewDetail} style={{ fontSize: 12.5, color: C.tealInk, fontWeight: 700, cursor: 'pointer' }}>
          View expenses →
        </span>
        <span style={{ fontSize: 11, color: C.sub, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          Helpful?
          <span style={{ display: 'inline-flex', gap: 4 }}>
            <Thumb onClick={() => onHelpful?.(true)} />
            <Thumb down onClick={() => onHelpful?.(false)} />
          </span>
        </span>
      </div>
    </div>
  );
}
